/**
 * Custom CKEditor Plugin for Font Color Editing
 * 
 * This module provides the implementation for editing and managing custom font color attributes within CKEditor.
 * It handles the conversion, schema extension, and command registration necessary for applying font colors to text.
 * 
 * ## Key Features
 * - **Upcast Conversion**: Maps custom font color attributes from view (HTML) elements to the model.
 *   - Supports `class` attributes on `<span>` elements for modern styling.
 *   - Backwards compatible with legacy `<font>` elements and `color` attributes.
 * - **Downcast Conversion**: Converts model attributes back into view elements to ensure proper rendering.
 * - **Schema Extension**: Adds support for `CustomFontColor` as a formatting attribute on text nodes.
 * - **Command Integration**: Registers a custom font color command (`CustomFontColorCommand`) for programmatic and UI interactions.
 * - **Attribute Properties**: Configures font color attributes to copy on Enter and behave as formatting.
 * 
 * ### Core Components
 * - **CustomFontColorEditing**: 
 *   - Registers upcast and downcast converters for handling custom font colors.
 *   - Extends the editor schema to support font color attributes.
 *   - Integrates the `CustomFontColorCommand` for managing font color changes.
 * 
 * ### Upcast Conversions
 * - `<span class="custom-color">Text</span>` → Maps `class` attribute to `CustomFontColor`.
 * - `<font class="custom-color">Text</font>` → Legacy support for `class` attribute mapping.
 * 
 * ### Downcast Conversion
 * - Model attributes are rendered as `<span>` elements with `class` attributes.
 * 
 * ### References
 * - CKEditor Conversion API: 
 *   https://ckeditor.com/docs/ckeditor5/latest/api/module_engine_conversion_conversion-Conversion.html
 * - CKEditor Schema API: 
 *   https://ckeditor.com/docs/ckeditor5/latest/api/module_engine_model_schema-Schema.html
 * - CKEditor Command API: 
 *   https://ckeditor.com/docs/ckeditor5/latest/api/module_core_command-Command.html
 * 
 * @module CustomFontColorEditing
 * @exports CustomFontColorEditing
 */

import { CKPlugins } from '../../constants/constants';
import { CustomFontColorCommand } from './fontcolorCommand';
import { CustomFontColorUI } from './fontcolorUI';

const { Plugin } = window.ClassicEditor;

export class CustomFontColorEditing extends Plugin {
	static get pluginName() {
		return 'CustomFontColorEditing';
	}

	static get requires() {
		return [ CustomFontColorEditing, CustomFontColorUI ];
	}

	constructor(editor) {
		super(editor);

		editor.conversion.for('upcast').elementToAttribute({
			view: {
				name: 'span',
				attributes: {
					class: true,
				}
			},
			model: {
				key: CKPlugins.CustomFontColor,
				value: viewElement => viewElement.getAttribute('class')
			}
		});

		// Support legacy `<font color="..">` formatting.
		editor.conversion.for('upcast').elementToAttribute({
			view: {
				name: 'font',
				attributes: {
					'class': /^#?\w+$/
				}
			},
			model: {
				key: CKPlugins.CustomFontColor,
				value: viewElement => viewElement.getAttribute('class')
			}
		});

		editor.conversion.for('downcast').attributeToElement( {
			model: CKPlugins.CustomFontColor,
			view: this.renderDowncastElement()
		} );

		editor.commands.add(CKPlugins.CustomFontColor, new CustomFontColorCommand(editor));

		// Allow the font color attribute on text nodes.
		editor.model.schema.extend('$text', { allowAttributes: [CKPlugins.CustomFontColor] });

		editor.model.schema.setAttributeProperties(CKPlugins.CustomFontColor, {
			isFormatting: true,
			copyOnEnter: true
		});

	}

	renderDowncastElement() {
		return (modelAttributeValue, { writer } ) => {
			return writer.createAttributeElement('span', {
				class: modelAttributeValue
			}, { priority: 7 });
		};
	}
}
